import { getToday } from "../utils";

// ═══════════════════════════════════════════════════════════════
// ABORDAGEM PARADA — a régua de quem entrou em conversa e sumiu
// ═══════════════════════════════════════════════════════════════
//
// "Abordagem" virou depósito: empresa que alguém contatou uma vez e nunca
// mais, e o funil continuava contando como conversa viva. O estágio não
// mente por si — o que faltava era medir o SILÊNCIO desde o último ato.
//
// A regra: passou de 2 dias sem contato registrado e sem justificativa
// vigente, a empresa precisa ser explicada. A justificativa NÃO muda o
// estágio — a empresa continua em abordagem esperando resposta do parceiro,
// porque é isso que está acontecendo de verdade. Ela só compra prazo até a
// data da próxima tentativa, e essa promessa fica registrada com autor.
//
// A justificativa mora DENTRO do histórico de estágio (stage_history), não em
// coluna própria: ela é um evento da trilha, e assim nenhuma gravação depende
// de coluna nova para funcionar — campo que o banco não conhece é descartado
// na hora de salvar, e a justificativa sumiria sem ninguém perceber.

export const PRAZO_ABORDAGEM_DIAS = 2;

export const MOTIVOS_PARADO = [
  { id: "sem_resposta", label: "Mandei mensagem e o parceiro não respondeu" },
  { id: "nao_atendeu", label: "Liguei e não atenderam" },
  { id: "responsavel_ausente", label: "Responsável não estava / de férias" },
  { id: "pediu_prazo", label: "Pediu para retornar depois" },
  { id: "analisando", label: "Está avaliando a proposta internamente" },
  { id: "nao_consegui", label: "Não consegui fazer o contato ainda" },
  { id: "outro", label: "Outro motivo" },
];

export const rotuloMotivo = (id) =>
  MOTIVOS_PARADO.find((m) => m.id === id)?.label || id || "sem motivo";

const DIA = 86400000;

export const diasEntre = (de, ate) => {
  if (!de || !ate) return 0;
  const a = Date.parse(`${String(de).slice(0, 10)}T00:00:00Z`);
  const b = Date.parse(`${String(ate).slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(a) || Number.isNaN(b)) return 0;
  return Math.floor((b - a) / DIA);
};

export const somarDias = (data, dias) =>
  new Date(Date.parse(`${String(data).slice(0, 10)}T00:00:00Z`) + dias * DIA)
    .toISOString()
    .slice(0, 10);

/**
 * A data do último ATO real nesta empresa: contato registrado, justificativa
 * dada, entrada em abordagem ou, na falta de tudo isso, o cadastro.
 *
 * O `createdAt` no fim da fila é de propósito: parceiro que alguém empurrou
 * para abordagem sem registrar nada (o velho "avançar estágio") não pode
 * aparecer como se tivesse movimento hoje.
 */
/** As justificativas desta empresa, na ordem em que foram dadas. */
export function justificativas(p = {}) {
  return (Array.isArray(p.stageHistory) ? p.stageHistory : []).filter((h) => h?.tipo === "justificativa");
}

export function ultimoMovimento(p = {}) {
  const datas = [
    p.lastContact,
    ...justificativas(p).map((j) => j?.date),
    ...(Array.isArray(p.interactions) ? p.interactions.map((i) => i?.date) : []),
    ...(Array.isArray(p.stageHistory) ? p.stageHistory.filter((h) => h?.to === "abordagem").map((h) => h?.date) : []),
    p.abordagemDesde,
    p.createdAt,
  ]
    .filter(Boolean)
    .map((d) => String(d).slice(0, 10))
    .sort();
  return datas.length ? datas[datas.length - 1] : null;
}

export function diasParado(p = {}, hoje = getToday()) {
  const d = ultimoMovimento(p);
  return d ? Math.max(0, diasEntre(d, hoje)) : 0;
}

/** A justificativa que ainda está de pé (a espera prometida não venceu). */
export function justificativaVigente(p = {}, hoje = getToday()) {
  const lista = justificativas(p);
  const ultima = lista[lista.length - 1];
  if (!ultima?.proximaTentativa || ultima.proximaTentativa < hoje) return null;
  // Contato registrado depois da justificativa encerra a espera: o parceiro
  // respondeu, e continuar mostrando "aguardando" seria mentira no card.
  if (p.lastContact && p.lastContact > ultima.date) return null;
  return ultima;
}

export function precisaJustificar(p, hoje = getToday()) {
  if (!p || p.stage !== "abordagem") return false;
  if (justificativaVigente(p, hoje)) return false;
  return diasParado(p, hoje) >= PRAZO_ABORDAGEM_DIAS;
}

/** Quem está devendo explicação, o mais parado primeiro. */
export function aCobrar(partners = [], hoje = getToday()) {
  return (partners || [])
    .filter((p) => precisaJustificar(p, hoje))
    .sort((a, b) => diasParado(b, hoje) - diasParado(a, hoje));
}

/**
 * Registra a justificativa. O estágio continua o mesmo — de propósito.
 * Quem está esperando resposta do parceiro está em abordagem, e fingir outra
 * coisa só esconderia o problema que este alerta existe para mostrar.
 */
export function aplicarJustificativa(p, { motivo, detalhe = "", proximaTentativa, autor = "" }, hoje = getToday()) {
  const parado = diasParado(p, hoje);
  const texto = `${rotuloMotivo(motivo)}${detalhe.trim() ? ` — ${detalhe.trim()}` : ""}`;
  return {
    ...p,
    nextAction: `Nova tentativa de contato (${rotuloMotivo(motivo)})`,
    nextActionDate: proximaTentativa,
    stageHistory: [
      ...(Array.isArray(p.stageHistory) ? p.stageHistory : []),
      {
        // from e to iguais de propósito: o estágio não muda, quem está
        // esperando resposta do parceiro está em abordagem.
        id: `just-${hoje}-${(Array.isArray(p.stageHistory) ? p.stageHistory.length : 0)}`,
        from: p.stage, to: p.stage, date: hoje, autor,
        tipo: "justificativa", motivo, detalhe: detalhe.trim(), proximaTentativa, diasParado: parado,
        note: `Sem retorno há ${parado} ${parado === 1 ? "dia" : "dias"} — ${texto}. Nova tentativa em ${proximaTentativa}.`,
      },
    ],
  };
}

// ── Comprovante do contato ──────────────────────────────────────
//
// Contato registrado sem prova era palavra contra palavra: o card dizia
// "abordagem" e ninguém sabia se a conversa existiu. O print do WhatsApp,
// com data e hora visíveis, é o que transforma o registro em trilha.

/** O canal em que o print é obrigatório — é o que a equipe usa de fato. */
export const CANAIS_COM_PRINT = ["WhatsApp"];

export const exigeComprovante = (canal) => CANAIS_COM_PRINT.includes(canal);

export const temComprovante = (interacao = {}) => !!interacao?.comprovantePath;

/** O último contato desta empresa que veio com prova anexada. */
export function ultimoComprovante(p = {}) {
  const lista = (Array.isArray(p.interactions) ? p.interactions : []).filter(temComprovante);
  return lista.length ? lista[lista.length - 1] : null;
}

/** Contatos registrados sem nenhuma prova — o que o gerente vai querer cobrar. */
export function contatosSemComprovante(p = {}) {
  return (Array.isArray(p.interactions) ? p.interactions : []).filter((i) => !temComprovante(i));
}
